import { downloadCSV } from 'react-admin';
import type { ClicksSummariesResource } from '../../../../Entities/ClicksSummary';
import { DateTime } from 'luxon';
import {
  escapeCsv,
  formatDateString,
  getReportDateLabels,
  type ReportFilter,
  type ReportGranularity,
} from '../../../../Components/ReportsWidget/utils';
import { SERVER_TIMEZONE } from '../../../../Components/TzDate/utils';
import { isNonEmptyString, removeUndefinedKeys } from '../../../../utils';

export interface PrefetchedData {
  clicks_summaries?: ClicksSummariesResource[];
}

export function getClicksSummaryReportParams(filter: ReportFilter) {
  if (!isNonEmptyString(filter.greaterOrEqual?.date)) {
    throw new Error('Clicks summary report requires `from` date');
  }
  if (!isNonEmptyString(filter.lessOrEqual?.date)) {
    throw new Error('Clicks summary report requires `to` date');
  }
  return {
    from: filter.greaterOrEqual.date,
    to: filter.lessOrEqual.date,
    granularity: filter.granularity === 'hourly' ? ('hourly' as const) : ('daily' as const),
  };
}

export const GETPRICE_REFNAME = 'getprice';
export const FUTURE_REFNAMES = ['future', 'future_publishing', 'gplink'] as const;

type RefnameFilter =
  | { kind: 'contains'; values: readonly string[] }
  | { kind: 'doesNotContain'; values: readonly string[] };

export function formatReportDate(
  value: string | undefined,
  granularity: ReportGranularity = 'daily',
): string {
  const date = value ? DateTime.fromISO(value, { zone: SERVER_TIMEZONE }) : null;

  if (!date?.isValid) {
    return '';
  }

  return granularity === 'hourly' ? date.toFormat('yyyy-LL-dd HH:00') : date.toFormat('yyyy-LL-dd');
}

function getClicksListFilter(
  date: DateTime,
  granularity: ReportGranularity,
  refnameFilter?: RefnameFilter,
) {
  const isRefnameContainsFilter = refnameFilter?.kind === 'contains';
  const isRefnameDoesNotContainFilter = refnameFilter?.kind === 'doesNotContain';

  return removeUndefinedKeys({
    greaterOrEqual: {
      timestamp: formatDateString(date.startOf(granularity === 'hourly' ? 'hour' : 'day')),
    },
    lessOrEqual: {
      timestamp: formatDateString(date.endOf(granularity === 'hourly' ? 'hour' : 'day')),
    },
    or: isRefnameContainsFilter ? { contains: { refname: [...refnameFilter.values] } } : undefined,
    not: isRefnameDoesNotContainFilter
      ? { contains: { refname: [...refnameFilter.values] } }
      : undefined,
  });
}

function getDisplayedFilters(refnameFilter?: RefnameFilter): Record<string, boolean> {
  const isRefnameContainsFilter = refnameFilter?.kind === 'contains';
  const isRefnameDoesNotContainFilter = refnameFilter?.kind === 'doesNotContain';

  return removeUndefinedKeys({
    'or.contains.refname': isRefnameContainsFilter ? true : undefined,
    'not.contains.refname': isRefnameDoesNotContainFilter ? true : undefined,
  });
}

export function getClicksListUrl(
  dateValue: string | undefined,
  granularity: ReportGranularity,
  refnameFilter?: RefnameFilter,
): string {
  const date = dateValue ? DateTime.fromISO(dateValue, { zone: SERVER_TIMEZONE }) : null;

  if (!date?.isValid) {
    return '/clicks';
  }

  const params = new URLSearchParams({
    filter: JSON.stringify(getClicksListFilter(date, granularity, refnameFilter)),
    displayedFilters: JSON.stringify(getDisplayedFilters(refnameFilter)),
  });

  return `/clicks?${params.toString()}`;
}

export function downloadClicksSummariesCsv(
  records: ClicksSummariesResource[],
  filter: ReportFilter,
): void {
  const { startDate, endDate } = getReportDateLabels(filter);
  const granularity = filter.granularity === 'hourly' ? 'hourly' : 'daily';

  const header = ['Date', 'Total Clicks', 'Getprice Clicks', 'Future Clicks', 'Other Clicks'].join(
    ',',
  );

  const rows = records.map((record) => {
    const attrs = record.attributes ?? {};

    return [
      escapeCsv(formatReportDate(attrs.date, granularity)),
      attrs.total_clicks ?? '',
      attrs.getprice_clicks ?? '',
      attrs.future_clicks ?? '',
      attrs.other_clicks ?? '',
    ].join(',');
  });

  downloadCSV([header, ...rows].join('\n'), `clicks_summary_${startDate}_through_${endDate}`);
}
